/**
 * 🔒 /admin/dfcl-binder — DFCL binder (discovery card) unlock panel.
 *
 * Owner-only. Lets Roc grant unlocks of binder-only DFCL discovery cards
 * (#243 ShadowFlame, #289 Bulldozer Jab, #327 Frontline Plate, and any
 * future `binder_only` cards) to a specific account by email — no curl.
 *
 * Flow mirrors /admin/dfcl-decks (deck grants):
 *   1. Look up a user by email → shows unlocked/locked state per card.
 *   2. Unlock a locked card ($addToSet — idempotent).
 *   3. Relock an unlocked card ($pull — idempotent).
 *
 * The binder catalog is driven by `/api/admin/dfcl/binder/lookup`, so
 * future binder-only cards appear here automatically. No client-side
 * card list to keep in sync.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Search, UserCheck, Check, X, AlertCircle, Lock, Unlock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDFCLBinder() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isOwner =
    user && (user.role === "owner" || (user.email || "").toLowerCase() === "omnimuscle@yahoo.com");

  const [email, setEmail]       = useState("");
  const [target, setTarget]     = useState(null);   // { user_id, email, name, binder_cards[], summary }
  const [lookupErr, setLookupErr] = useState(null);
  const [looking, setLooking]   = useState(false);
  const [busyCard, setBusyCard] = useState(null);   // card_id currently being toggled

  useEffect(() => { document.title = "🔒 DFCL Binder · Admin"; }, []);
  if (loading) return null;
  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400" data-testid="admin-dfcl-binder-locked">
        🔒 Owner only.
      </div>
    );
  }

  async function lookUp(e) {
    e?.preventDefault?.();
    setLookupErr(null);
    setTarget(null);
    const clean = (email || "").trim().toLowerCase();
    if (!clean) {
      setLookupErr("Enter an email.");
      return;
    }
    setLooking(true);
    try {
      const { data } = await axios.get(`${API}/admin/dfcl/binder/lookup`, {
        params: { email: clean },
      });
      setTarget(data);
    } catch (err) {
      setLookupErr(err?.response?.data?.detail || "Lookup failed.");
    } finally {
      setLooking(false);
    }
  }

  async function refreshTarget() {
    if (!target?.email) return;
    try {
      const { data } = await axios.get(`${API}/admin/dfcl/binder/lookup`, {
        params: { email: target.email },
      });
      setTarget(data);
    } catch (err) {
      // non-fatal; the single-card response already updated summary
    }
  }

  async function unlock(card_id) {
    if (!target) return;
    setBusyCard(card_id);
    try {
      const { data } = await axios.post(`${API}/admin/dfcl/binder/unlock`, {
        card_id,
        target_email: target.email,
      });
      if (data.did_unlock) {
        toast.success(`Unlocked "${card_id}" for ${target.name || target.email}.`);
      } else {
        toast.info(`${target.name || target.email} already had "${card_id}". No change.`);
      }
      await refreshTarget();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlock failed.");
    } finally {
      setBusyCard(null);
    }
  }

  async function relock(card_id) {
    if (!target) return;
    if (!window.confirm(`Relock "${card_id}" from ${target.name || target.email}?`)) return;
    setBusyCard(card_id);
    try {
      const { data } = await axios.post(`${API}/admin/dfcl/binder/relock`, {
        card_id,
        target_email: target.email,
      });
      toast.success(`Relocked "${card_id}" for ${target.name || target.email}.`);
      await refreshTarget();
      void data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Relock failed.");
    } finally {
      setBusyCard(null);
    }
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-8 text-neutral-200" data-testid="admin-dfcl-binder-page">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate("/admin")}
        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-amber-200 mb-5"
        data-testid="admin-dfcl-binder-back"
      >
        <ArrowLeft size={14} /> Back to Admin
      </button>

      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">🔒 DFCL</div>
        <h1 className="font-display text-3xl text-white mt-1">Binder Unlocks</h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-xl leading-snug">
          Grant Dragonious Flame Card Legends discovery cards to specific accounts.
          Binder cards start locked for every player — unlocks reveal the full art and rules.
          Idempotent — safe to click twice.
        </p>
      </div>

      {/* ─── Lookup form ──────────────────────────────────────── */}
      <form
        onSubmit={lookUp}
        className="border border-zinc-800 bg-black/40 p-4 mb-5"
        data-testid="admin-dfcl-binder-lookup-form"
      >
        <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
          Player Email
        </label>
        <div className="flex gap-2 mt-1.5">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="player@example.com"
            data-testid="admin-dfcl-binder-email-input"
            className="flex-1 bg-black border border-zinc-700 px-3 py-2 text-sm text-white focus:border-amber-400 focus:outline-none"
            autoCapitalize="off"
            autoCorrect="off"
          />
          <button
            type="submit"
            disabled={looking || !email}
            data-testid="admin-dfcl-binder-lookup-btn"
            className="inline-flex items-center gap-2 border border-amber-500/50 bg-amber-950/30 hover:border-amber-400 hover:bg-amber-950/50 text-amber-200 px-4 py-2 text-[10px] uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search size={14} /> {looking ? "Looking…" : "Look Up"}
          </button>
        </div>
        {lookupErr && (
          <div className="mt-2 flex items-center gap-2 text-xs text-red-300" data-testid="admin-dfcl-binder-lookup-err">
            <AlertCircle size={14} /> {lookupErr}
          </div>
        )}
      </form>

      {/* ─── Target player ────────────────────────────────────── */}
      {target && (
        <div
          className="border border-amber-500/40 bg-gradient-to-br from-[#1a1308] to-black p-4 mb-5"
          data-testid="admin-dfcl-binder-target"
        >
          <div className="flex items-center gap-3 flex-wrap">
            <UserCheck size={18} className="text-amber-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-display text-lg text-white leading-tight">
                {target.name}
              </div>
              <div className="text-[11px] text-neutral-400 font-mono truncate">
                {target.email} · {target.role || "user"} · <span className="text-neutral-600">{target.user_id?.slice(0, 10)}…</span>
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-500/40 bg-amber-950/30 px-2 py-1 shrink-0" data-testid="admin-dfcl-binder-counter">
              {target.summary?.unlocked ?? 0} / {target.summary?.total ?? 0} unlocked
            </div>
          </div>
        </div>
      )}

      {/* ─── Binder catalog with per-card action ─────────────────── */}
      {target && (
        <div className="space-y-2" data-testid="admin-dfcl-binder-catalog">
          <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
            Binder · {target.binder_cards?.length ?? 0} discovery cards
          </div>
          {(target.binder_cards || []).map((c) => {
            const unlocked = !c.locked;
            const busy = busyCard === c.id;
            // When unlocked, c has full fields (name, art_url, rarity_meta, etc.).
            // When locked, c is a mask — we fall back to the card_number for identity.
            const label = unlocked
              ? c.name
              : `??? · #${c.card_number ?? "—"}`;
            const r = c.rarity_meta || {};
            return (
              <div
                key={c.id}
                data-testid={`admin-dfcl-binder-row-${c.id}`}
                className="border border-zinc-800 bg-black/40 p-3 flex items-center gap-3 flex-wrap"
              >
                {unlocked && c.art_url ? (
                  <img
                    src={c.art_url}
                    alt=""
                    className="w-10 h-14 object-cover border"
                    style={{ borderColor: r.frame_hex || "#3f3f46" }}
                  />
                ) : (
                  <div
                    className="w-10 h-14 border-2 border-dashed flex items-center justify-center shrink-0"
                    style={{ borderColor: (r.frame_hex || "#f59e0b") + "88", background: "repeating-linear-gradient(45deg, #0F0A08 0, #0F0A08 4px, #141015 4px, #141015 8px)" }}
                  >
                    <Lock size={14} className="opacity-60" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-base text-white leading-tight">{label}</span>
                    <span
                      className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border"
                      style={{ color: r.frame_hex || "#a1a1aa", borderColor: (r.frame_hex || "#3f3f46") + "77" }}
                    >
                      {r.label || c.rarity}
                    </span>
                  </div>
                  <div className="text-[11px] text-neutral-500 font-mono">{c.id}</div>
                </div>
                {unlocked ? (
                  <>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-500/40 bg-emerald-950/20 px-2 py-1">
                      <Check size={11} /> Unlocked
                    </span>
                    <button
                      onClick={() => relock(c.id)}
                      disabled={busy}
                      data-testid={`admin-dfcl-binder-relock-${c.id}`}
                      className="inline-flex items-center gap-1.5 border border-red-500/40 hover:border-red-400 text-red-300 hover:text-red-200 px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <X size={12} /> {busy ? "…" : "Relock"}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => unlock(c.id)}
                    disabled={busy}
                    data-testid={`admin-dfcl-binder-unlock-${c.id}`}
                    className="inline-flex items-center gap-1.5 border border-amber-500/50 bg-amber-950/30 hover:border-amber-400 hover:bg-amber-950/50 text-amber-200 px-4 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-50"
                  >
                    <Unlock size={12} /> {busy ? "Unlocking…" : "Unlock →"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!target && !looking && !lookupErr && (
        <div className="text-center text-neutral-500 text-sm py-8 italic" data-testid="admin-dfcl-binder-hint">
          Look up a player by email to see which discovery cards they've unlocked and grant more.
        </div>
      )}
    </div>
  );
}
